/**
 * DisputeStatusPill — colored pill showing the current dispute status
 * with i18n label. 7 states map to a 4-color palette so the visual
 * distinction reads at a glance without becoming a rainbow.
 *
 * Memo'd because it renders inside the dispute list rows where a
 * realtime status change shouldn't re-render the parent.
 */

import { memo } from 'react'
import { View, Text } from 'react-native'
import { useTranslation } from 'react-i18next'
import { useTheme } from '../../lib/hooks/use-theme'
import { useSemanticColors, SPACING, RADIUS, withAlpha } from '../../lib/theme/tokens'
import { COLORS, FONTS } from '../../lib/brand'
import type { DisputeStatus } from '../../lib/types/dispute.types'

interface DisputeStatusPillProps {
  status: DisputeStatus
}

function statusTone(status: DisputeStatus): 'neutral' | 'amber' | 'coral' | 'success' {
  switch (status) {
    case 'open':
    case 'awaiting_response':
      return 'amber'
    case 'under_review':
      return 'neutral'
    case 'resolved_claimant':
    case 'resolved_split':
      return 'success'
    case 'resolved_respondent':
    case 'withdrawn':
      return 'neutral'
    default:
      return 'neutral'
  }
}

function DisputeStatusPillBase({ status }: DisputeStatusPillProps) {
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const tone = statusTone(status)

  /**
   * One tone colour per state, resolved per theme. It paints the fill and the
   * border only — never the label.
   *
   * The label used to be painted in the tone itself, which is where the
   * contrast went: a mid-tone amber/green on a 14%-alpha wash of itself
   * measured 2.50:1 over the dark surface, far under the 4.5:1 AA floor for
   * text this size. No colour in the palette can be both the fill and a
   * readable label on that fill in both themes, so the label is
   * `textPrimary` (>10:1 either way) and the tone stays purely decorative —
   * safe, because the pill also spells the status out in words.
   */
  const toneColor = (() => {
    switch (tone) {
      case 'amber':
        return isDark ? COLORS.amber.DEFAULT : COLORS.amber.dark
      case 'coral':
        return COLORS.coral.DEFAULT
      case 'success':
        return colors.success
      case 'neutral':
      default:
        return colors.textMuted
    }
  })()

  return (
    <View
      accessibilityRole="text"
      accessibilityLabel={t(`disputes.status_${status}`)}
      style={{
        alignSelf: 'flex-start',
        paddingHorizontal: SPACING.sm + SPACING.xs / 2,
        paddingVertical: SPACING.xs,
        borderRadius: RADIUS.full,
        backgroundColor: withAlpha(toneColor, isDark ? 0.22 : 0.16),
        borderWidth: 1,
        borderColor: withAlpha(toneColor, isDark ? 0.45 : 0.35),
      }}
    >
      <Text
        style={{
          // `bodyBold` is reserved for ≥17px; this is a 12px label, which is
          // also the minimum readable size the design system allows.
          fontFamily: FONTS.body,
          fontSize: 12,
          letterSpacing: 0.3,
          color: colors.textPrimary,
        }}
      >
        {t(`disputes.status_${status}`).toUpperCase()}
      </Text>
    </View>
  )
}

export const DisputeStatusPill = memo(DisputeStatusPillBase)
